/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

/**
 * LayoutPageSection — Accordion: picks the Page Builder pages used as the list
 * and detail layouts when view_type is 'custom'.
 *
 * Only pages with page_type 'record_template' are offered. An ordinary page is
 * reachable at /<slug> and is not written against a record, so rendering one
 * here would show raw {{ }} placeholders to visitors.
 *
 * Leaving a picker empty is valid and means "use the built-in renderer for
 * that surface" — the server then returns a null layout
 * (build_custom_layout_payload) and the public page falls back, so a
 * half-configured custom view is never broken.
 *
 * The placeholder syntax shown in the hint uses the table's own name, because
 * the template alias IS the table name — nothing here is specific to any one
 * application's schema.
 */

import React from 'react';

const PICKERS = [
  {
    key: 'detail_layout_page',
    label: 'Detail page layout',
    hint: 'Shown at /p/<table>/<id> — one record.',
  },
  {
    key: 'list_layout_page',
    label: 'List page layout',
    hint: 'Shown at /p/<table> — repeated for each record. Wrap anything that '
        + 'belongs to the whole page, rather than to one record, in '
        + '<div data-arcee-page> and it renders once at the top.',
    // Enabled now that the list route consumes a custom layout. It was disabled
    // while the column existed but nothing read it, so a builder could pick a
    // layout that was then silently ignored.
  },
];

export default function LayoutPageSection({ config, updateConfig, theme, pages, tableName }) {
  const templatePages = pages || [];
  const alias = tableName || 'table_name';

  if (templatePages.length === 0) {
    return (
      <div className="small text-muted">
        No record template pages yet. Create one in Page Builder and set its type to
        <span className="fw-semibold"> Record Template</span>, then choose it here.
      </div>
    );
  }

  return (
    <div>
      <div className="small text-muted mb-3">
        Choose the Page Builder pages that lay out this table's public pages. Write{' '}
        <code>{`{{ ${alias}.field_name }}`}</code> in their HTML to place record data.
      </div>

      {PICKERS.map(({ key, label, hint, disabled }) => (
        <div className="mb-3" key={key}>
          <label className="form-label small fw-semibold" htmlFor={`layout-${key}`}>
            {label}{disabled && <em className="text-muted fw-normal"> (not available yet)</em>}
          </label>
          <select
            id={`layout-${key}`}
            className="form-select form-select-sm"
            disabled={disabled}
            value={disabled ? '' : (config?.[key] ?? '')}
            onChange={e =>
              updateConfig({ [key]: e.target.value ? Number(e.target.value) : null })
            }
            style={{ borderColor: '#e2e8f0' }}
          >
            <option value="">— Use built-in layout —</option>
            {templatePages.map(p => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
          <div className="form-text small" style={{ color: theme?.primary ? '#64748b' : '#64748b' }}>
            {hint}
          </div>
        </div>
      ))}
    </div>
  );
}
